import React, {useEffect, useState} from "react";
import {useIntl} from "react-intl";
import {gql} from "@apollo/client";
import LoginLayout from "../layouts/LoginLayout";
import Banner from "../layouts/Banner"
// eslint-disable-next-line import/named
import {Route, Routes, useNavigate} from "react-router-dom";
import client from "../config/GraphQLApolloClient";
import {
    clearStorage,
    getCurrentLoggedInUser,
    getJwtPromise,
    getLastDocumentReferrer,
    getParameterByName,
    getRealm,
    isValidReferrer,
    replaceUrlDomain,
    setLastDocumentReferrer,
    setRealm,
    storeInitialPathParameters
} from "./OauthUtils";
import MainLayout from "../layouts/MainLayout";
import {PortalUser} from "../components";
import CallbackLayout from "../layouts/WebEAMCallbackLayout";
import {LicenseManager} from "ag-grid-enterprise";
import PortalLoading from "../layouts/PortalLoading";
import LogOutLayout from "../layouts/LogOutLayout";
import {entitlementLookup} from "./entitlementService";
import {
    getUserEntitlementResultByUserId_getUserEntitlementResultByUserId_partners
} from "./__generated__/getUserEntitlementResultByUserId";

LicenseManager.setLicenseKey(
    // tslint:disable-next-line
    "CompanyName=Titus Corporation_on_behalf_of_BMW SA,LicensedApplication=eInvoicing,LicenseType=SingleApplication,LicensedConcurrentDeveloperCount=1,LicensedProductionInstancesCount=0,AssetReference=AG-029969,SupportServicesEnd=5_July_2023_[v2]_MTY4ODUxMTYwMDAwMA==8fdb15c1328ca21d51cc402a3f21b986"
);

const QUERY_GET_WEBEAM_VALIDATE_TOKEN = gql`
    query OAuth_getWebEamValidateToken($token: String!) {
        OAuth_getWebEamValidateToken(token: $token)
    }
`;

const TECHNICAL_FAILURE_MESSAGE =
    "Technical failure: We are currently unable to connect to backend services. Please log an incident or contact support.\nTechnischer Fehler: Es besteht derzeit keine Verbindung zu den Backend-Services. Bitte erstellen Sie ein Incident-Ticket oder kontaktieren Sie den Support.";

const isBackendUnavailableError = (err: any): boolean => {
    const message = String(
        err?.message || err?.networkError?.message || err?.toString?.() || ""
    ).toLowerCase();
    const statusCode = err?.networkError?.statusCode || err?.statusCode || err?.status;

    if (typeof statusCode === "number" && statusCode >= 500) {
        return true;
    }

    return (
        message.includes("network") ||
        message.includes("failed to fetch") ||
        message.includes("fetch failed") ||
        message.includes("request timed out") ||
        message.includes("timeout") ||
        message.includes("econnrefused") ||
        message.includes("503") ||
        message.includes("502") ||
        message.includes("500")
    );
};


const App = () => {
    const intl = useIntl();
    const [currentUser, setCurrentUser] = useState<PortalUser>(undefined);
    const [userLoading, isUserLoading] = useState<boolean>(true);
    const [userLogout, isUserLogout] = useState<boolean>(false);
    const [validToken, isValidToken] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>(undefined);
    const [authorized, isAuthorized] = useState<boolean>(undefined);
    const [userDistinctEntitlements, setUserDistinctEntitlements] = useState<string[]>(undefined);
    const [entitlementPartner, setEntitlementPartner] = useState<getUserEntitlementResultByUserId_getUserEntitlementResultByUserId_partners[]>(undefined);
    const [technicalFailure, setTechnicalFailure] = useState<boolean>(false);
    const navigate = useNavigate();
    const getUserDistinctEntitlements = () => {
        const userName = getJwtPromise()?.sub;
        entitlementLookup(userName)
            .then((data) => {
                const foundEntitlements = data?.getUserEntitlementResultByUserId?.distinctEntitlements;
                setEntitlementPartner(data?.getUserEntitlementResultByUserId?.partners);
                if (foundEntitlements === null || foundEntitlements === undefined || foundEntitlements.length === 0) {
                    console.error("NO USER ENTITLEMENTS FOUND");
                    setUserDistinctEntitlements([]);
                } else {
                    setUserDistinctEntitlements(foundEntitlements);
                }
            });

    };

    useEffect(() => {
        storeInitialPathParameters();
        setRealm("", "init clear");
        getRealm(true);//call getRealm here to ensure we save the initial realm passed in via query string
        console.warn(`Referred from:: ${document.referrer}`);
        setLastDocumentReferrer();
        const token = localStorage.getItem("access_token");
        if (token !== null && token !== undefined && token !== "undefined") {
            client
                .query({
                    query: QUERY_GET_WEBEAM_VALIDATE_TOKEN,
                    variables: {token},
                    fetchPolicy: "network-only"
                })
                .then((respond) => {
                   isValidToken(true);
                   isAuthorized(true);
                   getUserDistinctEntitlements();
                   sessionStorage.removeItem("sessionExpired");
                   //sessionStorage.removeItem("callBackAlreadyPerformed");
                    console.log(respond)
                })
                .catch((err) => {
                    console.error(`Validate token failed: ${err}`);
                    if (isBackendUnavailableError(err)) {
                        setTechnicalFailure(true);
                        setErrorMessage(TECHNICAL_FAILURE_MESSAGE);
                        isUserLoading(false);
                        return;
                    }

                    console.log("Calling clear storage on token validation failed");
                    clearStorage();
                    isAuthorized(false);
                    isValidToken(false);
                    sessionStorage.setItem("sessionExpired","true");
                    console.log(err)
                });
        } else {
            if (window.location.pathname.indexOf("logout") === 1) {
                navigate("/logout");
                isUserLoading(true);
            } else {
                isAuthorized(false);
            }
        }
    }, []);

    useEffect(() => {
        if (!validToken || currentUser !== undefined) {
            return;
        }

        Promise.resolve(getCurrentLoggedInUser())
            .then((user) => {
                setCurrentUser(user);
                isUserLoading(false);
                return user;
            })
            .then(user => {
                if (user.dateFormat) {
                    sessionStorage.setItem('date-format', user.dateFormat.toUpperCase());
                }
                if (user.language !== localStorage.getItem("locale")) {
                    localStorage.setItem("locale", user.language)
                    window.location.reload();
                }
            })
            .catch((err) => {
                console.log(err)
                if (isBackendUnavailableError(err)) {
                    setTechnicalFailure(true);
                    setErrorMessage(TECHNICAL_FAILURE_MESSAGE);
                    isUserLoading(false);
                    return;
                }

                setErrorMessage(err.message);
                isUserLoading(false);
                console.log("Could not determine current user clearing storage");
                if (!isValidReferrer(getLastDocumentReferrer()) && !getParameterByName("realm"))
                {
                    clearStorage();
                    window.location.href = "/logout";
                }

            });
    }, [currentUser, validToken]);

    if (technicalFailure) {
        return <PortalLoading userLoading={false} errorMessage={errorMessage || TECHNICAL_FAILURE_MESSAGE}/>;
    }

    if (technicalFailure) {
        return <PortalLoading userLoading={false} errorMessage={errorMessage || TECHNICAL_FAILURE_MESSAGE}/>;
    }


    return (
        <>

            {false === authorized && <LoginLayout/>}


            <Routes>
                <Route key={"main"} path={"*"}
                       element={
                           <div translate={"no"}>
                               <MainLayout currentUser={currentUser} intl={intl}
                                           distinctEntitlements={userDistinctEntitlements} entitlementPartner={entitlementPartner}/>

                           </div>
                       }/>
                <Route key={"authorization"} path={"/authorization"} element={<LoginLayout/>}/>
                <Route key={"callback"} path={"/callback"} element={<CallbackLayout/>}/>
                <Route key={"logout"} path={"/logout"}
                       element={<LogOutLayout authenticated={false} oAuthUrl={""} error={""} isReady={false}/>}/>


            </Routes>


        </>
    );
};
export default App;